import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Badge, Modal, Alert } from 'react-bootstrap';
import { useAppStore } from '../../store/appStore';
import { apiService } from '../../services/api';
import { Appointment } from '../../types';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfirmModal from '../common/ConfirmModal';

const AppointmentsPage: React.FC = () => {
  const {
    appointments,
    appointmentsMeta,
    loading,
    setAppointments,
    setLoading,
  } = useAppStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [alert, setAlert] = useState<{ variant: 'success' | 'danger'; message: string } | null>(null);

  const loadAppointments = useCallback(async () => {
    setLoading('appointments', true);
    try {
      const response = await apiService.getAppointments({
        page: currentPage,
        limit: recordsPerPage,
        search: searchTerm || undefined,
      });
      setAppointments(response.data || [], response.meta_data);
    } catch (error) {
      console.error('Failed to load appointments:', error);
      setAlert({ variant: 'danger', message: 'Failed to load appointments' });
    } finally {
      setLoading('appointments', false);
    }
  }, [currentPage, recordsPerPage, searchTerm, setLoading, setAppointments]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadAppointments();
  };

  const handleRecordsPerPageChange = (newRecordsPerPage: number) => {
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1); // Reset to first page when changing records per page
  };

  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;

    try {
      await apiService.cancelAppointment(selectedAppointment._id);
      setShowCancelModal(false);
      setSelectedAppointment(null);
      setAlert({ variant: 'success', message: 'Appointment cancelled successfully' });
      // Reload appointments to reflect the cancellation status
      loadAppointments();
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
      setAlert({ variant: 'danger', message: 'Failed to cancel appointment' });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (isCancelled: boolean) => {
    return isCancelled ? (
      <Badge bg="danger">Cancelled</Badge>
    ) : (
      <Badge bg="success">Active</Badge>
    );
  };

  const getAppointmentTypeBadge = (type: string) => {
    const colors: { [key: string]: string } = {
      'consultation': 'primary',
      'follow-up': 'info',
      'initial': 'warning',
    };
    return <Badge bg={colors[type] || 'secondary'}>{type}</Badge>;
  };


  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>Appointments Management</h2>
        </Col>
      </Row>

      {alert && (
        <Row className="mb-3">
          <Col>
            <Alert
              variant={alert.variant}
              onClose={() => setAlert(null)}
            >
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Form onSubmit={handleSearch}>
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Search</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          placeholder="Search by name or email..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Button variant="outline-secondary" type="submit">
                          Search
                        </Button>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="admin-card">
            <Card.Body className="admin-card-body p-0">
              {loading.appointments ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0 modern-table">
                    <thead className="sticky-header">
                      <tr className="table-header">
                        <th className="border-0 py-3">
                          <i className="bi bi-person me-2 text-primary"></i>User
                        </th>
                        <th className="border-0 py-3">
                          <i className="bi bi-telephone me-2 text-primary"></i>Contact
                        </th>
                        <th className="border-0 py-3">
                          <i className="bi bi-calendar-date me-2 text-primary"></i>Date
                        </th>
                        <th className="border-0 py-3">
                          <i className="bi bi-clock me-2 text-primary"></i>Time
                        </th>
                        <th className="border-0 py-3">
                          <i className="bi bi-tag me-2 text-primary"></i>Type
                        </th>
                        <th className="border-0 py-3">
                          <i className="bi bi-gear me-2 text-primary"></i>Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-5">
                            <div className="text-muted">
                              <i className="bi bi-calendar-x fs-1 mb-3 d-block"></i>
                              <h5 className="mb-2">No appointments found</h5>
                              <p className="mb-0">No appointments match your current search criteria.</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        appointments.map((appointment, index) => (
                          <tr 
                            key={appointment._id} 
                            className={`table-row ${index % 2 === 0 ? 'even-row' : 'odd-row'} clickable-row`}
                            onClick={() => {
                              setSelectedAppointment(appointment);
                              setShowDetailsModal(true);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <td className="py-3">
                              <div>
                                <strong className="fs-6">{appointment.first_name} {appointment.last_name}</strong>
                                <br />
                                <small className="text-muted">{appointment.email}</small>
                              </div>
                            </td>
                            <td className="py-3">
                              <span className="badge bg-secondary bg-opacity-10 text-dark">
                                {appointment.contact_no}
                              </span>
                            </td>
                            <td className="py-3">
                              <span className="fw-medium">{formatDate(appointment.date)}</span>
                            </td>
                            <td className="py-3">
                              <span className="badge bg-info bg-opacity-10 text-info">
                                {appointment.slot}
                              </span>
                            </td>
                            <td className="py-3">{getAppointmentTypeBadge(appointment.appointment_type)}</td>
                            <td className="py-3">
                              <div className="d-flex gap-2">
                                {appointment.is_cancelled ? (
                                  <Badge bg="danger" className="px-3 py-2">
                                    <i className="bi bi-x-circle me-1"></i>
                                    Cancelled
                                  </Badge>
                                ) : (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedAppointment(appointment);
                                      setShowCancelModal(true);
                                    }}
                                    title="Cancel appointment"
                                  >
                                    <i className="bi bi-x-circle me-1"></i>
                                    Cancel
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Enhanced Pagination */}
      <Row className="mt-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <span className="pagination-info me-3">
                Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, appointmentsMeta.total)} of {appointmentsMeta.total} appointments
              </span>
            </div>
            
            <div className="d-flex align-items-center gap-3">
              {/* Page Size Selector */}
              <div className="d-flex align-items-center">
                <span className="pagination-info me-2">Show:</span>
                <Form.Select
                  size="sm"
                  style={{ width: '80px' }}
                  value={recordsPerPage}
                  onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Form.Select>
              </div>

              {/* Pagination Controls */}
              {appointmentsMeta.totalPages > 1 && (
                <div className="d-flex align-items-center gap-1 pagination-controls">
                  {/* First Page */}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                    title="First Page"
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </Button>
                  
                  {/* Previous Page */}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    title="Previous Page"
                  >
                    <i className="bi bi-chevron-left"></i>
                  </Button>

                  {/* Page Numbers */}
                  <div className="d-flex gap-1 mx-2">
                    {(() => {
                      const pages = [];
                      const totalPages = appointmentsMeta.totalPages;
                      const current = currentPage;
                      
                      // Show up to 5 page numbers
                      let startPage = Math.max(1, current - 2);
                      let endPage = Math.min(totalPages, current + 2);
                      
                      // Adjust if we're near the beginning or end
                      if (endPage - startPage < 4) {
                        if (startPage === 1) {
                          endPage = Math.min(totalPages, startPage + 4);
                        } else {
                          startPage = Math.max(1, endPage - 4);
                        }
                      }
                      
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Button
                            key={i}
                            variant={i === current ? "primary" : "outline-primary"}
                            size="sm"
                            onClick={() => setCurrentPage(i)}
                            className="px-3"
                          >
                            {i}
                          </Button>
                        );
                      }
                      
                      return pages;
                    })()}
                  </div>

                  {/* Next Page */}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={currentPage === appointmentsMeta.totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    title="Next Page"
                  >
                    <i className="bi bi-chevron-right"></i>
                  </Button>
                  
                  {/* Last Page */}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={currentPage === appointmentsMeta.totalPages}
                    onClick={() => setCurrentPage(appointmentsMeta.totalPages)}
                    title="Last Page"
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Appointment Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Appointment Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAppointment && (
            <div className="row">
              <div className="col-md-6">
                <h6>User Information</h6>
                <p><strong>Name:</strong> {selectedAppointment.first_name} {selectedAppointment.last_name}</p>
                <p><strong>Email:</strong> {selectedAppointment.email}</p>
                <p><strong>Contact:</strong> {selectedAppointment.contact_no}</p>
              </div>
              <div className="col-md-6">
                <h6>Appointment Details</h6>
                <p><strong>Type:</strong> {selectedAppointment.appointment_type}</p>
                <p><strong>Consultant:</strong> {selectedAppointment.consultant_name}</p>
                <p><strong>Date:</strong> {formatDate(selectedAppointment.date)}</p>
                <p><strong>Time:</strong> {selectedAppointment.slot}</p>
                <p><strong>Duration:</strong> {selectedAppointment.slot_duration} minutes</p>
                <p><strong>Price:</strong> ${selectedAppointment.price}</p>
                <p><strong>Status:</strong> {getStatusBadge(selectedAppointment.is_cancelled)}</p>
                {selectedAppointment.meeting_type && (
                  <p><strong>Meeting Type:</strong> {selectedAppointment.meeting_type}</p>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <ConfirmModal
        show={showCancelModal}
        title="Cancel Appointment"
        message={`Are you sure you want to cancel the appointment for ${selectedAppointment?.first_name} ${selectedAppointment?.last_name}?`}
        confirmText="Cancel Appointment"
        variant="danger"
        onConfirm={handleCancelAppointment}
        onCancel={() => setShowCancelModal(false)}
      />
    </Container>
  );
};

export default AppointmentsPage;
